import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LOGO = "https://customer-assets-eiarnc6j.emergentagent.net/job_a2603301-837e-467e-952a-d7e2edb4c197/artifacts/3ut7qksg_file_0000000043b081f5a40b26af34420fa3.webp";

const LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/before-after", label: "Before / After" },
  { to: "/guarantees", label: "Guarantees" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || !isHome;

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-[100] transition-colors duration-500 ${
        solid ? "bg-black/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link data-testid="nav-logo" to="/" className="flex items-center gap-3">
          <img src={LOGO} alt="GI Remodeling" className="h-11 w-11 object-contain" />
          <span className="font-serif text-xl tracking-wide hidden sm:block">
            <span className="gold-gradient-text">GI</span> <span className="silver-gradient-text">Remodeling</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `link-underline text-sm font-body transition-colors ${isActive ? "text-gold" : "text-white/80 hover:text-white"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-5">
          <Link
            data-testid="nav-portal-link"
            to="/portal"
            className="hidden md:inline-flex link-underline text-sm font-body text-white/80 hover:text-white transition-colors"
          >
            Client Portal
          </Link>
          <Link
            data-testid="nav-quote-btn"
            to="/contact"
            className="hidden md:inline-flex items-center bg-gold text-black text-xs font-semibold uppercase tracking-widest px-6 py-3 hover:bg-white transition-colors duration-300"
          >
            Request a Quote
          </Link>
          <button data-testid="mobile-menu-btn" className="lg:hidden text-white" onClick={() => setOpen(!open)}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-black/95 border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)} className="font-serif text-2xl text-white/90">
                  {l.label}
                </NavLink>
              ))}
              <Link to="/portal" onClick={() => setOpen(false)} className="font-serif text-2xl text-white/90">Client Portal</Link>
              <Link to="/contact" onClick={() => setOpen(false)} className="mt-2 bg-gold text-black text-sm font-semibold uppercase tracking-widest px-6 py-3 text-center">
                Request a Quote
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
